import { Box, Chip, IconButton, Snackbar, Stack } from "@mui/material";
import { Alert, Button, Typography } from "@platform/design-system";
import { useOutletContext, useParams } from "react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";

import { ROUTE_NAMES } from "../../constants";
import { formatDate } from "../../utils";
import { useScenarioDetailsQuery } from "../../hooks/useBbe";
import { useScenarioDetailsActions } from "../../hooks/useScenarioDetailsActions";

import { BackNavigationButton } from "./components/BackNavigationButton";
import {
  ScenarioDetailsTable,
  ScenarioDetailsTableHandle,
} from "./components/ScenarioDetailsTable";
import { ScenarioActionDrawer } from "./components/ScenarioActionDrawer/ScenarioActionDrawer";
import {
  DRAWER_ACTIONS,
  SCENARIO_ACTION_DRAWER_TYPE,
  SCENARIO_MODEL_MODE,
  ScenarioActionDrawerType,
  ScenarioModelModeType,
} from "./constants";
import {
  BBEOutletContext,
  ScenarioEdit,
  ScenarioEditType,
  ScenarioLOC,
} from "./types";
import { CarrierSelect } from "./components/carrierSelect";
import { AddNewBusinessFormHandle } from "./components/ScenarioActionDrawer/AddNewBusinessForm/AddNewBusinessForm";
import { ScenarioModelModal } from "./components/ScenarioModelModal";
import { ScenarioDetailsActionsNullifiedModal } from "./components/ScenarioDetailsActionsNullifiedModal";

type ScenarioEditRow = {
  editId: string;
  // Every edit on this coverage. A coverage can carry more than one edit, but
  // the row only surfaces one editId, removing a change must delete them all.
  editIds: string[];
  scenarioChange?: ScenarioEditType | null;
};

// A coverage (baseline LOC) can have multiple edits, but its row surfaces only
// one editId. Map each coverage to all of its edit ids so removal can delete
// every one and the change fully reverts.
const buildEditIdsByLocId = (
  edits: ScenarioEdit[] = [],
): Map<string, string[]> => {
  const map = new Map<string, string[]>();
  for (const edit of edits) {
    if (!edit.sourceBaselineLocId) continue;
    map.set(edit.sourceBaselineLocId, [
      ...(map.get(edit.sourceBaselineLocId) ?? []),
      edit.id,
    ]);
  }
  return map;
};

export const ScenarioDetails: React.FC = () => {
  const { setSelectedModelId } = useOutletContext<BBEOutletContext>();

  const {
    id: brokerIdParam,
    modelId,
    scenarioId: scenarioIdParam,
  } = useParams<{
    id: string;
    modelId: string;
    scenarioId: string;
  }>();
  const brokerId = brokerIdParam ?? "";
  const scenarioId = scenarioIdParam ?? "";

  useEffect(() => {
    if (modelId) {
      setSelectedModelId(modelId);
    }
  }, [modelId, setSelectedModelId]);

  const {
    data: scenarioData,
    isLoading,
    isError,
    isRefetching,
  } = useScenarioDetailsQuery(scenarioId);

  const data = scenarioData?.scenario;
  const rows = scenarioData?.rows ?? [];

  const editIdsByLocId = useMemo(
    () => buildEditIdsByLocId(scenarioData?.edits),
    [scenarioData],
  );

  const date = formatDate(data?.createdAt, true, {
    hour: "numeric",
    minute: "numeric",
  })?.replace(",", "");

  const [drawerType, setDrawerType] = useState<ScenarioActionDrawerType | null>(
    null,
  );
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [carrier, setCarrier] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [removeMigrateModalType, setRemoveMigrateModalType] =
    useState<ScenarioModelModeType | null>(null);
  const [pendingRemoveRows, setPendingRemoveRows] = useState<ScenarioEditRow[]>(
    [],
  );

  const [nullifiedModalLines, setNullifiedModalLines] = useState<string[]>([]);

  const tableRef = useRef<ScenarioDetailsTableHandle>(null);

  const onClose = useCallback(() => {
    setDrawerType(null);
    setCarrier(null);
  }, [setDrawerType]);

  const getSnackbarMessage = useCallback(
    (type: ScenarioActionDrawerType | null, count?: number) => {
      switch (type) {
        case SCENARIO_ACTION_DRAWER_TYPE.LostBOR: {
          return `${count} Coverage(s) Lost BOR`;
        }
        case SCENARIO_ACTION_DRAWER_TYPE.Terminate: {
          return `${count} Coverage(s) Terminated`;
        }
        case SCENARIO_ACTION_DRAWER_TYPE.Migrate: {
          return carrier?.label
            ? `${count} Coverage(s) Migrated to ${carrier.label}`
            : `${count} Coverage(s) Migrated`;
        }
        case SCENARIO_ACTION_DRAWER_TYPE.AddNew:
          return "Coverage(s) Added";
        default:
          return `${count} Change(s) Removed`;
      }
    },
    [carrier?.label],
  );

  const formRef = useRef<AddNewBusinessFormHandle>(null);
  const {
    editScenario,
    removeChangeScenario,
    selectedRows,
    setSelectedRows,
    disabledRemoveChangeBtn,
    onClickScenarioActionConfirm,
  } = useScenarioDetailsActions({
    baselineId: modelId,
    scenarioId,
    drawerType,
    formRef,
    getSnackbarMessage,
    setSnackbarMessage,
    setSnackbarOpen,
    setCarrier,
    carrier,
    tableRef,
    setNullifiedModalLines,
  });

  const handleSelectionChanged = useCallback(
    (rows: ScenarioLOC[]) => {
      setSelectedRows(rows);
    },
    [setSelectedRows],
  );

  const drawerOpen = drawerType !== null;

  const onClickClearCoverage = useCallback(
    () => tableRef.current?.clearSelection(),
    [],
  );

  const onClickDrawer = useCallback((value: ScenarioActionDrawerType) => {
    setDrawerType(value);
    setCarrier(null);
  }, []);

  const removeChangeMutation = useCallback(
    async (scenarioEditRows: ScenarioEditRow[]) => {
      // A coverage can have several edits (and a migrate renders as two rows
      // sharing one editId) — collect the full, de-duplicated set to delete.
      const uniqueEditIds = [
        ...new Set(scenarioEditRows.flatMap((row) => row.editIds)),
      ];

      const results = await Promise.allSettled(
        uniqueEditIds.map((editId) => removeChangeScenario.mutateAsync(editId)),
      );

      const deletedEditIds = new Set(
        uniqueEditIds.filter(
          (_, index) => results[index].status === "fulfilled",
        ),
      );

      if (deletedEditIds.size === 0) return;

      tableRef.current?.clearSelection();
      setCarrier(null);
      setRemoveMigrateModalType(null);
      setPendingRemoveRows([]);

      const revertedRowCount = scenarioEditRows.filter((row) =>
        row.editIds.every((editId) => deletedEditIds.has(editId)),
      ).length;
      const notRevertedCount = selectedRows.length - revertedRowCount;

      setSnackbarMessage(getSnackbarMessage(null, revertedRowCount));
      setSnackbarOpen(true);

      setNullifiedModalLines(
        notRevertedCount > 0
          ? [
              `${revertedRowCount} record(s) with change(s) were reverted.`,
              `${notRevertedCount} record(s) without change(s) were not affected.`,
            ]
          : [],
      );
    },
    [removeChangeScenario, selectedRows.length, getSnackbarMessage],
  );

  const onRemoveChange = useCallback(() => {
    const scenarioEditRows: ScenarioEditRow[] = selectedRows.flatMap((row) => {
      if (row.editId == null) return [];
      return [
        {
          editId: row.editId,
          editIds: [
            ...new Set([row.editId, ...(editIdsByLocId.get(row.id) ?? [])]),
          ],
          scenarioChange: row.scenarioChange,
        },
      ];
    });

    if (scenarioEditRows.length === 0) return;

    const migrateRows = scenarioEditRows.filter(
      (row) => row.scenarioChange === ScenarioEditType.Migrate,
    );

    if (migrateRows?.length > 0) {
      setRemoveMigrateModalType(SCENARIO_MODEL_MODE.RemoveMigrateChange);
      setPendingRemoveRows(scenarioEditRows);

      if (migrateRows.length === scenarioEditRows.length) return;

      setRemoveMigrateModalType(SCENARIO_MODEL_MODE.RemoveChangePartialMigrate);
      return;
    }

    removeChangeMutation(scenarioEditRows);
  }, [removeChangeMutation, selectedRows, editIdsByLocId]);

  const actionDrawerAdditionalContent = useMemo(() => {
    if (drawerType !== SCENARIO_ACTION_DRAWER_TYPE.Migrate) {
      return undefined;
    }

    const selectedCarriers = [
      ...new Set(selectedRows.map((row) => row.carrierId)),
    ].map((carrierId) => ({ carrierId }));
    // for our workflow, we only want to filter our carriers if the user selects
    // rows with the SAME carrier. If there are multiple selection of carriers within
    // the rows selected, then do not filter out any carriers.
    const filter =
      selectedCarriers.length === 1 ? selectedCarriers[0] : undefined;

    return (
      <CarrierSelect
        value={carrier}
        onChange={setCarrier}
        textFieldProps={{ helperText: "Destination carrier" }}
        filter={filter}
      />
    );
  }, [carrier, drawerType, selectedRows]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        flex: 1,
        position: "relative",
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          flexShrink: 1,
          minWidth: 0,
          overflowY: "auto",
          marginRight: drawerOpen ? "30%" : 0,
          transition: "margin-right 0.3s ease",
        }}
      >
        <Stack direction="column" spacing={1}>
          <Stack spacing={0} direction="column">
            <BackNavigationButton
              label="Scenario Modeling"
              route={`/${ROUTE_NAMES.BBE}/${brokerId}/${ROUTE_NAMES.BBE_SCENARIO_MODELING}`}
              variant="body-medium"
            />

            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
              <Stack direction="column" spacing={0}>
                <Typography variant="header-small">
                  <strong>{data?.name}</strong>
                </Typography>

                <Typography variant="body-medium" color="textSecondary">
                  Created {date}
                </Typography>
              </Stack>

              <Stack
                direction="row"
                spacing={1}
                alignItems="flex-end"
                justifyContent="space-between"
                flex={1}
              >
                {selectedRows.length > 0 && (
                  <Chip
                    size="medium"
                    variant="filled"
                    color="default"
                    deleteIcon={<CancelIcon />}
                    label={`${selectedRows.length} Coverage(s) Selected`}
                    onDelete={() => onClickClearCoverage()}
                    sx={{ borderRadius: 1 }}
                  />
                )}

                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="flex-end"
                  alignItems="center"
                  flex={1}
                  useFlexGap
                  flexWrap="wrap"
                  sx={{ "& > button": { whiteSpace: "nowrap" } }}
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={
                      selectedRows?.length === 0 || disabledRemoveChangeBtn
                    }
                    onClick={onRemoveChange}
                  >
                    Remove Change
                  </Button>
                  {DRAWER_ACTIONS.map(({ type, label }) => (
                    <Button
                      key={type}
                      size="sm"
                      variant="secondary"
                      onClick={() => onClickDrawer(type)}
                    >
                      {label}
                    </Button>
                  ))}
                </Stack>
              </Stack>
            </Stack>
          </Stack>

          {isError ? (
            <Alert severity="error">
              Failed to load scenario details. Please try again.
            </Alert>
          ) : (
            <ScenarioDetailsTable
              ref={tableRef}
              rows={rows}
              onSelectionChanged={handleSelectionChanged}
              loading={isLoading || isRefetching}
            />
          )}
        </Stack>
      </Box>

      <ScenarioActionDrawer
        open={drawerOpen && Boolean(scenarioId)}
        drawerType={drawerType}
        coverageCount={selectedRows?.length}
        onClickClear={onClickClearCoverage}
        onClickConfirm={onClickScenarioActionConfirm}
        formRef={formRef}
        additionalContent={actionDrawerAdditionalContent}
        isAdditionalContentValid={
          drawerType === SCENARIO_ACTION_DRAWER_TYPE.Migrate
            ? carrier !== null
            : undefined
        }
        isLoading={editScenario.isPending}
        onClose={onClose}
      />

      {removeMigrateModalType && (
        <ScenarioModelModal
          open={true}
          mode={removeMigrateModalType}
          onClose={() => {
            setRemoveMigrateModalType(null);
          }}
          onConfirm={() => removeChangeMutation(pendingRemoveRows)}
          loading={removeChangeScenario.isPending}
        />
      )}
      <ScenarioDetailsActionsNullifiedModal
        lines={nullifiedModalLines}
        open={nullifiedModalLines.length > 0}
        onClose={() => setNullifiedModalLines([])}
      />

      <Snackbar
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        sx={{ position: "fixed" }}
        ContentProps={{
          sx: { "& .MuiSnackbarContent-message": { whiteSpace: "pre-line" } },
        }}
        action={
          <IconButton
            size="small"
            color="inherit"
            onClick={() => setSnackbarOpen(false)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
        autoHideDuration={5000}
      />
    </Box>
  );
};
